/**
 * Icon — Unified icon component for the entire application.
 *
 * Resolves icon references from multiple sources:
 *  1. Lucide icon names (kebab-case or PascalCase) → color Lucide component
 *  2. Emoji strings → rendered as-is with proper sizing
 *  3. ReactNode → rendered as-is (for custom SVG icons)
 *
 * All icons render with consistent sizing and optional color tinting.
 *
 * Wave A9.13: UI Modernization — Icon Restoration
 */
import React from 'react';
import * as LucideIcons from 'lucide-react';

// Color palette for plugin icons (vibrant, modern)
const ICON_COLORS: Record<string, string> = {
  // Core
  'layout-dashboard': 'var(--nc-color-primary)',
  'message-square': 'var(--nc-color-primary)',
  'settings': 'var(--nc-color-text-secondary)',
  'search': 'var(--nc-color-primary)',
  // Productivity
  'list-checks': '#10b981',       // emerald
  'check-square': '#10b981',
  'calendar': '#f59e0b',          // amber
  'clock': '#6366f1',             // indigo
  'timer': '#6366f1',
  'kanban': '#8b5cf6',            // violet
  'columns': '#8b5cf6',
  'target': '#ef4444',            // red
  'flag': '#ef4444',
  // Knowledge
  'file-text': '#3b82f6',         // blue
  'book-open': '#3b82f6',
  'notebook': '#3b82f6',
  'sticky-note': '#fbbf24',       // yellow
  'bookmark': '#f97316',          // orange
  'brain': '#ec4899',             // pink
  'lightbulb': '#fbbf24',
  'network': '#06b6d4',           // cyan
  'git-branch': '#06b6d4',
  // Media
  'image': '#a855f7',             // purple
  'video': '#dc2626',             // red
  'music': '#ec4899',             // pink
  'mic': '#f43f5e',               // rose
  'camera': '#8b5cf6',
  // Code & Dev
  'code': '#22c55e',              // green
  'terminal': '#22c55e',
  'cpu': '#64748b',               // slate
  'circuit-board': '#64748b',
  'bug': '#ef4444',
  'wrench': '#94a3b8',
  'tool': '#94a3b8',
  // People & Social
  'users': '#6366f1',
  'user': '#6366f1',
  'heart': '#ef4444',
  'share-2': '#3b82f6',
  'mail': '#3b82f6',
  // Location
  'map-pin': '#10b981',
  'map': '#10b981',
  'compass': '#06b6d4',
  // System
  'shield': '#f59e0b',
  'lock': '#f59e0b',
  'bell': '#f97316',
  'download': '#3b82f6',
  'upload': '#3b82f6',
  'folder': '#f59e0b',
  'file': '#94a3b8',
  'database': '#6366f1',
  'server': '#64748b',
  'globe': '#06b6d4',
  'store': '#f97316',
  'package': '#8b5cf6',
  'puzzle': '#ec4899',
  'zap': '#f59e0b',
  'activity': '#10b981',
  'bar-chart': '#3b82f6',
  'pie-chart': '#8b5cf6',
  'trending-up': '#10b981',
  // AI
  'bot': '#8b5cf6',
  'sparkles': '#f59e0b',
  'wand-2': '#a855f7',
};

/**
 * Convert kebab-case or camelCase icon name to PascalCase for Lucide lookup.
 */
function toPascalCase(name: string): string {
  return name
    .split(/[-_]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

/**
 * Check if a string is an emoji (starts with non-ASCII or known emoji patterns).
 */
function isEmoji(str: string): boolean {
  // Quick heuristic: if the first character is outside basic ASCII, it's likely an emoji
  const code = str.codePointAt(0);
  return code != null && code > 127;
}

export interface IconProps {
  /** Icon identifier: Lucide name (kebab-case or PascalCase), emoji, or 'none' */
  name?: string;
  /** Size in pixels (default: 18) */
  size?: number;
  /** Custom color override (CSS value) */
  color?: string;
  /** Apply the color mapping (default: true). Set to false for monochrome */
  colorful?: boolean;
  /** CSS class name */
  className?: string;
  /** Inline style overrides */
  style?: React.CSSProperties;
  /** A name that is neither an emoji nor a Lucide icon: shown as text (default) or not at all. */
  fallback?: 'text' | 'none';
}

export function Icon({
  name,
  size = 18,
  color,
  colorful = true,
  className = '',
  style,
  fallback = 'text',
}: IconProps) {
  if (!name) return null;

  // If it's an emoji, render as span
  if (isEmoji(name)) {
    return (
      <span
        className={`nc-icon nc-icon--emoji ${className}`}
        style={{ fontSize: size, lineHeight: 1, ...style }}
        role="img"
        aria-hidden="true"
      >
        {name}
      </span>
    );
  }

  // Try to resolve as a Lucide icon
  const pascalName = toPascalCase(name);
  const LucideIcon = (LucideIcons as Record<string, any>)[pascalName];

  if (LucideIcon) {
    const iconColor = color || (colorful ? ICON_COLORS[name.toLowerCase()] || ICON_COLORS[name] : undefined);
    return (
      <LucideIcon
        className={`nc-icon nc-icon--lucide ${className}`}
        size={size}
        color={iconColor}
        style={style}
        aria-hidden="true"
      />
    );
  }

  // Fallback: render as text (could be a short label or unknown icon)
  if (fallback === 'none') return null;
  return (
    <span
      className={`nc-icon nc-icon--text ${className}`}
      style={{ fontSize: size * 0.8, lineHeight: 1, ...style }}
      aria-hidden="true"
    >
      {name}
    </span>
  );
}

export default Icon;
